import { useState, useEffect, useContext, createContext } from 'react';

export const periods = ['tnx_min', 'tnx_max', 'daily', 'weekly', 'monthly'] as const;

export type Period = typeof periods[number];

type Currency = string;

export type Fees = {
  operation: number;
  min: number;
  fixed: number;
  conversion: number;
};

type CurrencyFees = Record<Currency, Fees>;

export type PaysystemLimitUsage = {
  limits: Record<Period, string | number>; // floats are converted to strings on the client
  usage: Record<Period, string | number>;
  rate: number;
};

type CurrencyPaysystemLimitUsage = Record<Currency, PaysystemLimitUsage>;

export type CurrencyGateways = Record<Currency, string>;
export type WithdrawableCard = { id: number; currency_gateways: CurrencyGateways };
type CardWithdrawalCards = Record<string, WithdrawableCard>;

type PlayerPaymentLimitUsage = {
  limit: string | number;
  usage: string | number;
  period: Period;
};

export type Locales = {
  personal_limit_label: Record<Period, string>;
  warnings?: Record<Currency, string>;
  service_provider_names: Record<string, string>;
  service_providers: string[];
  banks: [string, string | number][] | string[];
  account_types: string[];
  profile_fields: Record<string, string>;
} & Record<string, string>;

export type PaymentFormProps = {
  locales: Locales;
  currencies: Currency[];
  url: string;
  twofa: { verify: boolean | null };
  extra: {
    bank_names: Record<Currency, string[]>;
    selected_bank_name: string;
    vendor_amounts: number[];
    cards: CardWithdrawalCards;
    first_name: string;
    last_name: string;
    country: string;
    networks: Record<Currency, [string, string]>;
    payments_with_tag: Currency[];
    payments_with_optional_tag: Currency[];
    email: string;
    phone: string;
    targets?: string[];
    paysystem_limit_usage: Record<string, CurrencyPaysystemLimitUsage>;
    fees: Record<string, CurrencyFees>;
    currency_gateways?: CurrencyGateways;
    cis_banks?: Array<{ id: string; name: string }>;
  };
  kind: string;
  paysystem: string | null;
  payment_provider: string | null;
  player_currency: Currency;
  balance: string | number;
  selected_currency: Currency | null;
  paysystem_limit_usage: CurrencyPaysystemLimitUsage;
  player_payment_limit_usage: PlayerPaymentLimitUsage | null;
  fees: CurrencyFees;
  profile_fields: string[];
};

export type FormContext = {
  url: PaymentFormProps['url'];
  kind: PaymentFormProps['kind'];
  paysystem: string;
  paymentProvider: string;
  currency: PaymentFormProps['player_currency'];
  balance: PaymentFormProps['balance'];
  selectedCurrency: PaymentFormProps['selected_currency'];
  currencies: PaymentFormProps['currencies'];
  setCurrencies: (v: PaymentFormProps['currencies']) => void;
  paysystemLimitsUsage: PaymentFormProps['paysystem_limit_usage'];
  setPaysystemLimitsUsage: (v: PaymentFormProps['paysystem_limit_usage']) => void;
  playerPaymentLimitUsage: PaymentFormProps['player_payment_limit_usage'];
  paysystemFees: PaymentFormProps['fees'];
  setPaysystemFees: (v: PaymentFormProps['fees']) => void;
  twofa: PaymentFormProps['twofa'];
  locales: PaymentFormProps['locales'];
  extra: PaymentFormProps['extra'];
  isDeposit: boolean;
  key: string;
  amountFieldName: string;
  isAmountLimited: boolean;
  currencyUsage: PaysystemLimitUsage;
  fees: Fees;
  dynamicConversionFee: number;
  rate: number;
  changeCurrencyGateways: (currencyGateways: CurrencyGateways) => void;
};

const nullUsage: PaysystemLimitUsage = {
  limits: { tnx_min: 0.0, tnx_max: 0.0, daily: 0, weekly: 0, monthly: 0 },
  usage: { tnx_min: 0.0, tnx_max: 0.0, daily: 0, weekly: 0, monthly: 0 },
  rate: 1,
};

const nullFees: Fees = {
  operation: 0,
  min: 0,
  fixed: 0,
  conversion: 0,
};

// eslint-disable-next-line max-lines-per-function
const useFormContext = (
  props: PaymentFormProps,
  vendorCurrency: Currency,
  changeVendorCurrency: (c: Currency) => void
): FormContext => {
  const [currencies, setCurrencies] = useState(props.currencies);
  const [paysystemFees, setPaysystemFees] = useState(props.fees);
  const [paysystemLimitsUsage, setPaysystemLimitsUsage] = useState(props.paysystem_limit_usage);

  const { url, kind, balance, twofa, locales, extra } = props;
  const paysystem = props.paysystem || '';
  const paymentProvider = props.payment_provider || '';
  const currency = props.player_currency;
  const selectedCurrency = props.selected_currency;
  const playerPaymentLimitUsage = props.player_payment_limit_usage;

  const isDeposit = kind === 'deposit';
  const key = [paysystem, paymentProvider].filter((x) => x.length > 0).join('/');
  const amountFieldName = isDeposit ? 'vendor_amount' : 'amount';
  const isAmountLimited = paysystem === 'ecp2025_deposit' && paymentProvider === 'cashtocode';

  const currencyUsage = (vendorCurrency && paysystemLimitsUsage[vendorCurrency]) || nullUsage;
  const fees = (vendorCurrency && paysystemFees[vendorCurrency]) || nullFees;
  const dynamicConversionFee = vendorCurrency === currency ? 0 : fees.conversion;
  const { rate } = currencyUsage;

  const changeCurrencyGateways: FormContext['changeCurrencyGateways'] = (currencyGateways: CurrencyGateways) => {
    const newCurrencies = Object.keys(currencyGateways);
    setCurrencies(newCurrencies);

    if (!vendorCurrency || !newCurrencies.includes(vendorCurrency)) {
      changeVendorCurrency(newCurrencies.includes(currency) ? currency : newCurrencies[0]);
    }

    const newPaysystemLimitsUsage: FormContext['paysystemLimitsUsage'] = {};
    const newPaysystemFees: FormContext['paysystemFees'] = {};
    newCurrencies.forEach((c) => {
      const gateway = currencyGateways[c];
      newPaysystemLimitsUsage[c] = extra.paysystem_limit_usage[gateway][c];
      newPaysystemFees[c] = extra.fees[gateway][c];
    });
    setPaysystemLimitsUsage(newPaysystemLimitsUsage);
    setPaysystemFees(newPaysystemFees);
  };

  useEffect(() => {
    const currencyGateways = extra.currency_gateways;
    if (!currencyGateways) return;

    changeCurrencyGateways(currencyGateways);
  }, []);

  return {
    url,
    kind,
    paysystem,
    paymentProvider,
    currency,
    balance,
    selectedCurrency,
    currencies,
    setCurrencies,
    paysystemLimitsUsage,
    setPaysystemLimitsUsage,
    playerPaymentLimitUsage,
    paysystemFees,
    setPaysystemFees,
    twofa,
    locales,
    extra,
    isDeposit,
    key,
    amountFieldName,
    isAmountLimited,
    currencyUsage,
    fees,
    dynamicConversionFee,
    rate,
    changeCurrencyGateways,
  };
};

const defaultContext: FormContext = {
  url: '',
  kind: '',
  paysystem: '',
  paymentProvider: '',
  currency: '',
  balance: 0,
  selectedCurrency: '',
  currencies: [],
  setCurrencies: () => {},
  paysystemLimitsUsage: {},
  setPaysystemLimitsUsage: () => {},
  playerPaymentLimitUsage: { limit: 0, usage: 0, period: 'daily' },
  paysystemFees: {},
  setPaysystemFees: () => {},
  twofa: { verify: false },
  locales: {
    personal_limit_label: {
      tnx_min: 'tnx_min',
      tnx_max: 'tnx_max',
      daily: 'daily',
      weekly: 'weekly',
      monthly: 'monthly',
    },
    warnings: undefined,
    service_provider_names: {},
    service_providers: [],
    banks: [],
    account_types: [],
  } as unknown as Locales,
  extra: {
    bank_names: {},
    selected_bank_name: '',
    vendor_amounts: [],
    cards: {},
    first_name: '',
    last_name: '',
    country: '',
    networks: {},
    payments_with_tag: [],
    payments_with_optional_tag: [],
    email: '',
    phone: '',
    paysystem_limit_usage: {},
    fees: {},
  },
  isDeposit: true,
  key: '',
  amountFieldName: '',
  isAmountLimited: false,
  currencyUsage: nullUsage,
  fees: nullFees,
  dynamicConversionFee: 1,
  rate: 1,
  changeCurrencyGateways: () => {},
};

export const FormCtx = createContext<FormContext>(defaultContext);

const context: () => FormContext = () => useContext<FormContext>(FormCtx);

export { context as useContext };

export default useFormContext;
